import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import PremiumUnlockCard from "../components/PremiumUnlockCard";
import DisclaimerBanner from "../components/DisclaimerBanner";
import DimensionRadar from "../components/DimensionRadar";
import { toast } from "sonner";
import { ArrowRight, Award, FileText, Download, Loader2, CheckCircle2, XCircle, Info } from "lucide-react";

export default function Result() {
  const { id } = useParams();
  const { t, tField, lang } = useI18n();
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentState, setPaymentState] = useState("idle");
  // idle | polling | paid | expired | error

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/results/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `iqlog-${String(id).slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const load = React.useCallback(() => {
    return api
      .get(`/results/${id}`)
      .then((res) => setResult(res.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // If user just logged in with a pending result_id, auto-attach
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem("iqlog_pending_result_id");
    if (pending === id) {
      api
        .post(`/results/${id}/attach`)
        .then(() => {
          localStorage.removeItem("iqlog_pending_result_id");
          load();
        })
        .catch(() => {
          localStorage.removeItem("iqlog_pending_result_id");
        });
    }
  }, [user, id, load]);

  // Poll payment status when returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    let attempts = 0;
    const max = 10;
    const interval = 2000;
    let cancelled = false;

    setPaymentState("polling");

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentState("paid");
          await load();
          // Clean up the URL so refresh doesn't re-poll
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          toast.success(t("premium.title"));
          return;
        }
        if (data.status === "expired") {
          setPaymentState("expired");
          return;
        }
        attempts += 1;
        if (attempts < max) {
          setTimeout(tick, interval);
        } else {
          setPaymentState("error");
        }
      } catch (e) {
        if (cancelled) return;
        attempts += 1;
        if (attempts < max) {
          setTimeout(tick, interval);
        } else {
          setPaymentState("error");
          toast.error(formatApiError(e));
        }
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("session_id")]);

  if (!result) {
    return <div className="py-32 text-center text-slate-400">Loading…</div>;
  }

  const typeLabel = tField(result.type_label);
  const isPremium = result.is_premium && result.ai_analysis;
  const isIQTest = result.diagnostic_id === "iq";
  // Extended IQ scale: 0→70, 50→125, 100→180
  const iqScore = isIQTest ? Math.round(70 + (result.overall * 1.1)) : null;
  const iqBand = (() => {
    if (!isIQTest || iqScore == null) return null;
    if (iqScore >= 175) return { label: "Legendary",         color: "#FFD700" };
    if (iqScore >= 160) return { label: "Profoundly Gifted", color: "#FF00C8" };
    if (iqScore >= 150) return { label: "Genius",            color: "#FF6B35" };
    if (iqScore >= 140) return { label: "Highly Gifted",     color: "#A855F7" };
    if (iqScore >= 130) return { label: "Gifted",            color: "#8B5CF6" };
    if (iqScore >= 120) return { label: "Superior",          color: "#3B82F6" };
    if (iqScore >= 110) return { label: "High Average",      color: "#00FF94" };
    if (iqScore >= 90)  return { label: "Average",           color: "#94A3B8" };
    if (iqScore >= 80)  return { label: "Low Average",       color: "#94A3B8" };
    return { label: "Borderline", color: "#64748B" };
  })();

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-12 lg:py-16 space-y-10">
      {/* Payment status banner (when returning from Stripe) */}
      {paymentState !== "idle" && (
        <PaymentStatusBanner state={paymentState} t={t} />
      )}

      {/* Header */}
      <div className="border-b border-[#1E2A3F] pb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-2">
          // {tField(result.diagnostic_title)}
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight uppercase text-white">
          {t("result.overall")}
        </h1>
      </div>

      {/* Score + type */}
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 bg-[#0F1525] border border-[#3B82F6]/40 text-white p-8 md:p-12 relative scanlines neon-glow-soft">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-3">
            {isIQTest ? "IQ SCORE" : t("result.overall")}
          </div>
          <div
            data-testid="result-overall-score"
            className="font-mono text-8xl md:text-9xl tracking-tight leading-none"
            style={{
              color: isIQTest && iqBand ? iqBand.color : "#3B82F6",
              textShadow: `0 0 24px ${(isIQTest && iqBand ? iqBand.color : "rgba(59,130,246,0.5)")}80`,
            }}
          >
            {isIQTest ? iqScore : result.overall}
          </div>
          <div className="mt-3 font-mono text-sm text-[#64748B]">
            {isIQTest
              ? <>IQ · extended scale 70–180</>
              : <>/ 100</>}
          </div>

          {isIQTest && iqBand && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: `${iqBand.color}55`, background: `${iqBand.color}15` }}>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: iqBand.color }}>
                {iqBand.label}
              </span>
            </div>
          )}

          {isIQTest && (
            <div className="mt-4 font-mono text-[10px] text-[#64748B] uppercase tracking-[0.18em]">
              raw {result.overall} / 100
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-[#1E2A3F]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
              {t("result.type_label")}
            </div>
            <div data-testid="result-type-label" className="font-display text-3xl tracking-tight uppercase text-white">
              {typeLabel}
            </div>
            {result.badge && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#3B82F6]/40 bg-[#3B82F6]/10">
                <Award className="h-3.5 w-3.5 text-[#3B82F6]" strokeWidth={1.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">
                  {result.badge}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-7 bg-[#0F1525] border border-[#1E2A3F] p-8 md:p-12">
          <div className="label-mono mb-4">// Overview</div>
          <p className="text-base text-[#94A3B8] leading-relaxed">
            {isPremium ? result.ai_analysis.summary : t("result.free_only_notice")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/catalog"
              data-testid="result-take-another-link"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA]"
            >
              {t("result.back_to_catalog")}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            {user && (
              <Link
                to="/mypage"
                data-testid="result-view-mypage-link"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#1E2A3F] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:border-[#3B82F6] hover:text-[#3B82F6]"
              >
                {t("result.view_mypage")}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Notice banner — for IQ test (and any diagnostic that ships a notice) */}
      {result.notice && (
        <div
          data-testid="result-notice-banner"
          className="border border-[#A855F7]/40 bg-[#A855F7]/10 p-4 flex items-start gap-3"
        >
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#A855F7]" strokeWidth={1.5} />
          <p className="text-xs text-[#94A3B8] leading-relaxed">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A855F7] mr-2">// NOTICE</span>
            {tField(result.notice)}
          </p>
        </div>
      )}

      {/* Premium section */}
      {isPremium ? (
        <div className="space-y-8">
          {/* PDF download row */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
              // PREMIUM REPORT
            </div>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              data-testid="result-download-pdf"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 font-mono text-[10px] uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-50"
            >
              {downloadingPdf ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
              ) : (
                <Download className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              {t("result.download_pdf")}
            </button>
          </div>
          {/* Dimensions + radar */}
          <div className="grid md:grid-cols-12 gap-8 bg-[#0F1525] border border-[#1E2A3F] p-8 md:p-12">
            <div className="md:col-span-5">
              <div className="label-mono mb-3">{t("premium.dimensions")}</div>
              <ul className="space-y-3">
                {Object.entries(result.dimensions || {}).map(([k, v]) => {
                  const label =
                    (result.dimension_labels?.[k] && (result.dimension_labels[k][lang] || result.dimension_labels[k].en)) || k;
                  return (
                    <li key={k} data-testid={`dimension-row-${k}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#94A3B8]">{label}</span>
                        <span className="font-mono text-[#3B82F6] tabular-nums">{v}</span>
                      </div>
                      <div className="h-1 bg-[#1E2A3F] overflow-hidden">
                        <div
                          className="h-full bg-[#3B82F6] progress-fill"
                          style={{ width: `${v}%`, boxShadow: "0 0 6px rgba(59,130,246,0.5)" }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="md:col-span-7">
              <DimensionRadar
                dimensions={result.dimensions}
                labels={result.dimension_labels}
                lang={lang}
              />
            </div>
          </div>

          {/* AI Strengths / Weaknesses / Improvements */}
          <div className="grid md:grid-cols-3 gap-6">
            <AnalysisCard
              testId="premium-strengths"
              title={t("premium.strengths")}
              items={result.ai_analysis.strengths}
              accent="bg-[#3B82F6] text-black"
            />
            <AnalysisCard
              testId="premium-weaknesses"
              title={t("premium.weaknesses")}
              items={result.ai_analysis.weaknesses}
              accent="bg-[#10B981] text-black"
            />
            <AnalysisCard
              testId="premium-improvements"
              title={t("premium.improvements")}
              items={result.ai_analysis.improvements}
              accent="bg-white text-black"
            />
          </div>

          {/* PDF stub */}
          <div className="border border-dashed border-[#1E2A3F] p-8 flex items-center justify-between bg-[#0F1525]">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 bg-[#3B82F6] text-black flex items-center justify-center">
                <FileText className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-display text-xl tracking-tight uppercase text-white">{t("premium.download_pdf")}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{t("premium.pdf_soon")}</div>
              </div>
            </div>
            <button
              disabled
              data-testid="download-pdf-button"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#1E2A3F] text-[#64748B] font-mono text-[11px] uppercase tracking-[0.2em] font-bold cursor-not-allowed"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={2} />
              PDF (A4)
            </button>
          </div>
        </div>
      ) : (
        <PremiumUnlockCard result={result} />
      )}

      <DisclaimerBanner />
    </div>
  );
}

function AnalysisCard({ testId, title, items, accent }) {
  return (
    <div data-testid={testId} className="bg-[#0F1525] border border-[#1E2A3F]">
      <div className={`px-6 py-4 ${accent}`}>
        <div className="font-display text-xl tracking-tight uppercase">{title}</div>
      </div>
      <ul className="p-6 space-y-3">
        {(items || []).map((it, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="font-mono text-[#3B82F6] shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-[#94A3B8]">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PaymentStatusBanner({ state, t }) {
  if (state === "polling") {
    return (
      <div
        data-testid="payment-status-polling"
        className="flex items-center gap-3 border border-[#3B82F6]/40 bg-[#3B82F6]/10 p-4 rounded-md"
      >
        <Loader2 className="h-5 w-5 text-[#3B82F6] animate-spin" />
        <div>
          <div className="font-display text-sm tracking-wide text-white">
            {t("payment.processing_title")}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1">
            {t("payment.processing_subtitle")}
          </div>
        </div>
      </div>
    );
  }
  if (state === "paid") {
    return (
      <div
        data-testid="payment-status-paid"
        className="flex items-center gap-3 border border-[#00FF94]/40 bg-[#00FF94]/10 p-4 rounded-md"
      >
        <CheckCircle2 className="h-5 w-5 text-[#00FF94]" />
        <div className="font-display text-sm tracking-wide text-white">
          {t("payment.success")}
        </div>
      </div>
    );
  }
  if (state === "expired" || state === "error") {
    return (
      <div
        data-testid="payment-status-error"
        className="flex items-center gap-3 border border-[#EF4444]/40 bg-[#EF4444]/10 p-4 rounded-md"
      >
        <XCircle className="h-5 w-5 text-[#EF4444]" />
        <div className="font-display text-sm tracking-wide text-white">
          {state === "expired" ? t("payment.expired") : t("payment.error")}
        </div>
      </div>
    );
  }
  return null;
}
